/**
 * 免密碼登入 / 註冊前端邏輯。
 *
 * 搭配 themes/blocksy-child/woocommerce/myaccount/form-login.php，
 * 後端為 WTOY\Modules\Auth\AuthAjaxHandler。
 *
 * 設計重點：
 *  - channel（簡訊 / Email）前端只做顯示切換，實際仍由後端決定與驗證
 *  - 驗證碼 6 格輸入支援自動跳格、退格回退、貼上自動分配
 *  - 送出中鎖住按鈕，避免重複點擊建出重複帳號
 */
(function () {
    'use strict';

    var cfg = window.WTOY_AUTH || {};
    var SMS_COUNTRY = cfg.sms_country || 'TW';
    var CODE_LENGTH = parseInt(cfg.code_length, 10) || 6;
    var DEFAULT_COOLDOWN = parseInt(cfg.cooldown, 10) || 60;

    var root = document.getElementById('wtoy-auth');
    if (!root || !cfg.ajaxurl) {
        return;
    }

    var alertBox = root.querySelector('[data-alert]');
    var titleEl = root.querySelector('[data-title]');

    // -------------------------------------------------------------------------
    // 共用
    // -------------------------------------------------------------------------

    function post(action, data) {
        var body = new URLSearchParams();
        body.append('action', action);
        body.append('nonce', cfg.nonce || '');
        Object.keys(data).forEach(function (key) {
            body.append(key, data[key]);
        });

        return fetch(cfg.ajaxurl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            body: body.toString()
        }).then(function (res) {
            return res.json().catch(function () {
                return { success: false, data: { message: '伺服器回應異常，請稍後再試。' } };
            });
        });
    }

    function showAlert(message, type) {
        if (!alertBox) {
            return;
        }
        alertBox.textContent = message;
        alertBox.className = 'wtoy-auth__alert is-' + (type || 'error');
        alertBox.hidden = false;
    }

    function clearAlert() {
        if (alertBox) {
            alertBox.hidden = true;
            alertBox.textContent = '';
        }
    }

    function messageOf(res, fallback) {
        return (res && res.data && res.data.message) || fallback;
    }

    // -------------------------------------------------------------------------
    // 單一面板（登入 / 註冊）
    // -------------------------------------------------------------------------

    function createPanel(form) {
        var mode = form.getAttribute('data-panel'); // login | register
        var countrySelect = form.querySelector('[data-country]');
        var dialLabel = form.querySelector('[data-dial]');
        var deliveryHint = form.querySelector('[data-delivery-hint]');
        var sendBtn = form.querySelector('[data-send]');
        var countdownEl = form.querySelector('[data-countdown]');
        var submitBtn = form.querySelector('[data-submit]');
        var refererInput = form.querySelector('[data-referer]');
        var notifyEmailInput = form.querySelector('[data-notify-email]');
        var codeBoxes = Array.prototype.slice.call(form.querySelectorAll('[data-code-box]'));
        var codeValue = form.querySelector('[data-code-value]');

        var channel = 'sms';
        var timerId = null;
        var busy = false;

        // ---- channel -------------------------------------------------------

        function country() {
            return countrySelect ? countrySelect.value : SMS_COUNTRY;
        }

        function setChannel(next) {
            channel = next;

            form.querySelectorAll('[data-channel-field]').forEach(function (field) {
                field.hidden = field.getAttribute('data-channel-field') !== next;
            });

            form.querySelectorAll('[data-channel]').forEach(function (btn) {
                var active = btn.getAttribute('data-channel') === next;
                btn.classList.toggle('is-active', active);
                btn.setAttribute('aria-selected', active ? 'true' : 'false');
            });

            if (deliveryHint) {
                deliveryHint.textContent = next === 'sms'
                    ? '驗證碼將透過 簡訊 (SMS) 發送至您的手機號碼。'
                    : '驗證碼將透過 Email 發送至您的信箱。';
            }

            // 換管道等於換識別碼，先前的驗證碼與倒數都不再適用
            resetCode();
            stopCountdown();
            clearAlert();
        }

        function identifierInput() {
            return form.querySelector('[data-identifier="' + channel + '"]');
        }

        function identifier() {
            var input = identifierInput();
            return input ? input.value.trim() : '';
        }

        // ---- 驗證碼輸入 ------------------------------------------------------

        function syncCode() {
            codeValue.value = codeBoxes.map(function (box) {
                return box.value;
            }).join('');
        }

        function resetCode() {
            codeBoxes.forEach(function (box) {
                box.value = '';
            });
            syncCode();
        }

        function focusBox(index) {
            if (index >= 0 && index < codeBoxes.length) {
                codeBoxes[index].focus();
                codeBoxes[index].select();
            }
        }

        function fillFrom(index, digits) {
            for (var i = 0; i < digits.length && index + i < codeBoxes.length; i++) {
                codeBoxes[index + i].value = digits.charAt(i);
            }
            syncCode();
            focusBox(Math.min(index + digits.length, codeBoxes.length - 1));
        }

        codeBoxes.forEach(function (box, index) {
            box.addEventListener('input', function () {
                var digits = box.value.replace(/\D/g, '');
                box.value = '';
                if (digits === '') {
                    syncCode();
                    return;
                }
                // 手機自動填入或貼上時，input 事件可能一次帶入整串
                fillFrom(index, digits);
            });

            box.addEventListener('keydown', function (e) {
                if (e.key === 'Backspace' && box.value === '') {
                    focusBox(index - 1);
                } else if (e.key === 'ArrowLeft') {
                    focusBox(index - 1);
                } else if (e.key === 'ArrowRight') {
                    focusBox(index + 1);
                }
            });

            box.addEventListener('paste', function (e) {
                e.preventDefault();
                var text = (e.clipboardData || window.clipboardData).getData('text') || '';
                fillFrom(index, text.replace(/\D/g, '').slice(0, CODE_LENGTH - index));
            });

            box.addEventListener('focus', function () {
                box.select();
            });
        });

        // ---- 倒數 ------------------------------------------------------------

        function stopCountdown() {
            if (timerId) {
                clearInterval(timerId);
                timerId = null;
            }
            if (countdownEl) {
                countdownEl.textContent = '';
            }
            sendBtn.disabled = false;
        }

        function startCountdown(seconds) {
            stopCountdown();
            var remaining = seconds;
            sendBtn.disabled = true;

            var tick = function () {
                if (remaining <= 0) {
                    stopCountdown();
                    return;
                }
                if (countdownEl) {
                    countdownEl.textContent = remaining + ' 秒後可重新發送';
                }
                remaining--;
            };

            tick();
            timerId = setInterval(tick, 1000);
        }

        // ---- 發送驗證碼 ------------------------------------------------------

        sendBtn.addEventListener('click', function () {
            if (busy || sendBtn.disabled) {
                return;
            }
            var value = identifier();
            if (value === '') {
                showAlert(channel === 'sms' ? '請先輸入手機號碼。' : '請先輸入 Email。');
                var input = identifierInput();
                if (input) {
                    input.focus();
                }
                return;
            }

            clearAlert();
            busy = true;
            sendBtn.disabled = true;
            var originalText = sendBtn.textContent;
            sendBtn.textContent = '發送中⋯';

            post('wtoy_auth_send_otp', {
                purpose: mode,
                channel: channel,
                country: country(),
                identifier: value
            }).then(function (res) {
                sendBtn.textContent = originalText;
                busy = false;

                if (res.success) {
                    showAlert(messageOf(res, '驗證碼已發送。'), 'success');
                    startCountdown(parseInt(res.data.cooldown, 10) || DEFAULT_COOLDOWN);
                    focusBox(0);
                    return;
                }

                showAlert(messageOf(res, '發送失敗，請稍後再試。'));
                if (res.data && res.data.cooldown) {
                    startCountdown(parseInt(res.data.cooldown, 10));
                } else {
                    sendBtn.disabled = false;
                }
            }).catch(function () {
                sendBtn.textContent = originalText;
                sendBtn.disabled = false;
                busy = false;
                showAlert('連線失敗，請檢查網路後再試。');
            });
        });

        // ---- 送出 ------------------------------------------------------------

        form.addEventListener('submit', function (e) {
            e.preventDefault();
            if (busy) {
                return;
            }

            syncCode();
            var value = identifier();

            if (value === '') {
                showAlert(channel === 'sms' ? '請輸入手機號碼。' : '請輸入 Email。');
                return;
            }
            if (codeValue.value.length !== CODE_LENGTH) {
                showAlert('請輸入完整的 ' + CODE_LENGTH + ' 碼驗證碼。');
                focusBox(0);
                return;
            }

            clearAlert();
            busy = true;
            submitBtn.disabled = true;
            var originalText = submitBtn.textContent;
            submitBtn.textContent = '處理中⋯';

            var payload = {
                channel: channel,
                country: country(),
                identifier: value,
                code: codeValue.value
            };
            if (mode === 'register' && refererInput) {
                payload.referer_code = refererInput.value.trim();
            }
            if (mode === 'register' && channel === 'sms' && notifyEmailInput) {
                var notifyEmail = notifyEmailInput.value.trim();
                if (notifyEmail !== '') {
                    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
                        busy = false;
                        submitBtn.disabled = false;
                        submitBtn.textContent = originalText;
                        showAlert('Email 格式錯誤，請重新確認，或留白不填。');
                        return;
                    }
                    payload.notify_email = notifyEmail;
                }
            }

            post(mode === 'register' ? 'wtoy_auth_register' : 'wtoy_auth_login', payload)
                .then(function (res) {
                    if (res.success) {
                        showAlert(messageOf(res, '成功，正在跳轉⋯'), 'success');
                        window.location.assign(res.data.redirect);
                        return;
                    }

                    busy = false;
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalText;
                    resetCode();
                    focusBox(0);
                    showAlert(messageOf(res, '驗證失敗，請稍後再試。'));
                })
                .catch(function () {
                    busy = false;
                    submitBtn.disabled = false;
                    submitBtn.textContent = originalText;
                    showAlert('連線失敗，請檢查網路後再試。');
                });
        });

        // ---- 初始化 ----------------------------------------------------------

        form.querySelectorAll('[data-channel]').forEach(function (btn) {
            btn.addEventListener('click', function () {
                setChannel(btn.getAttribute('data-channel'));
            });
        });

        if (countrySelect) {
            countrySelect.addEventListener('change', function () {
                if (dialLabel) {
                    var match = (cfg.countries || []).filter(function (item) {
                        return item.code === countrySelect.value;
                    })[0];
                    dialLabel.textContent = match ? match.dial_code : '+886';
                }
                // 註冊管道由國家決定：台灣簡訊，其他 Email
                setChannel(countrySelect.value === SMS_COUNTRY ? 'sms' : 'email');
            });
        }

        setChannel(mode === 'register' && country() !== SMS_COUNTRY ? 'email' : 'sms');

        return {
            reset: function () {
                resetCode();
                stopCountdown();
            }
        };
    }

    // -------------------------------------------------------------------------
    // 登入 / 註冊切換
    // -------------------------------------------------------------------------

    var panels = {};
    root.querySelectorAll('[data-panel]').forEach(function (form) {
        if (form.tagName === 'FORM') {
            panels[form.getAttribute('data-panel')] = createPanel(form);
        }
    });

    function modeButton(mode) {
        return root.querySelector('[data-mode="' + mode + '"]');
    }

    root.querySelectorAll('[data-mode]').forEach(function (btn) {
        btn.addEventListener('click', function () {
            var mode = btn.getAttribute('data-mode');

            root.querySelectorAll('[data-mode]').forEach(function (other) {
                var active = other === btn;
                other.classList.toggle('is-active', active);
                other.setAttribute('aria-selected', active ? 'true' : 'false');
            });

            root.querySelectorAll('form[data-panel]').forEach(function (form) {
                var active = form.getAttribute('data-panel') === mode;
                form.hidden = !active;
                form.classList.toggle('is-active', active);
            });

            if (titleEl) {
                titleEl.textContent = mode === 'register' ? '註冊' : '登入';
            }

            clearAlert();
            if (panels[mode]) {
                panels[mode].reset();
            }

            // 讓網址可分享 / 可重整回同一個分頁。用 replaceState 而非 hash 指派，
            // 避免在瀏覽紀錄堆一堆只是切分頁的項目。
            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', '#' + mode);
            }
        });
    });

    // Deep link：header 的「註冊」按鈕連到 /my-account/#register 就會直接開註冊分頁。
    // 用 hash 而不是 query string，不會被頁面快取分版本，也不會和 WP 的 action 參數衝突。
    var initialMode = (window.location.hash || '').replace('#', '');
    if (initialMode === 'register' || initialMode === 'login') {
        var target = modeButton(initialMode);
        if (target && !target.classList.contains('is-active')) {
            target.click();
        }
    }
})();
